import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QuizNodeActivityEditor from '../../NodeActivityEditor/nodes/QuizNodeActivityEditor';
import RemoveIcon from '../../../../public/assets/images/editor/delete-icon-black.svg';
import MoveUpIcon from '../../../icons/move-up-black.svg';
import UploadImageIcon from '../../../icons/upload-image.svg';
import {
  ContentSection,
  Questions,
  QuestionWrapper,
  Question,
  QuestionTools,
  DeleteQuestion,
  MoveUpQuestion,
  MoveDownQuestion,
  InsertImage,
} from '../styles';

const WrittenQuizContentEditor = ({
  children,
  data,
  payload,
  setPayload,
  getUpdates,
  type,
  fileService,
  loading = false,
  updating = false,
}) => {
  const isUpdating = loading || updating;
  const useAutoSave = window.editor.useAutoSave;

  const mapQuestions = (questions) => {
    return questions.map((question) => ({
      ...question,
    }));
  };

  const [questions, setQuestions] = useState(mapQuestions(data?.QuizQuestions) || []);

  useEffect(() => {
    setPayload({ ...payload, questions });
  }, []);

  const addQuestion = (event) => {
    const newQuestions = [...(event?.detail?.questions || [])];
    newQuestions.push({ body: '', order: newQuestions.length });
    setQuestions(newQuestions);
  };

  useEffect(() => {
    document.addEventListener('question.added', addQuestion);
    return () => {
      document.removeEventListener('question.added', addQuestion);
    };
  }, []);

  useEffect(() => {
    const { itemsAddedOrRemoved, itemsUpdated, deletedIds } = getUpdates(mapQuestions(data?.QuizQuestions), questions);
    if (!isUpdating && (itemsAddedOrRemoved || itemsUpdated)) {
      setPayload({
        ...payload,
        questions,
        deletions: { questions: deletedIds },
        immediateSave: itemsAddedOrRemoved,
      });
    }
  }, [JSON.stringify(questions)]);

  useEffect(() => {
    if (questions?.length > data?.QuizQuestions?.length) {
      document.querySelector('.content-panel')?.scrollTo(0, 10000000);
    }
  }, [questions]);

  const isOrderDisabled = (index, delta) =>
    loading || (index === 0 && delta === -1) || (index === questions.length - 1 && delta === 1);

  const removeQuestion = (index) => {
    if (isUpdating) {
      return;
    }
    const newQuestions = questions
      .filter((question) => question.order !== index)
      .map((question, index) => ({ ...question, order: index }));
    setQuestions(newQuestions);
  };

  const orderQuestion = (index, delta) => {
    if (isUpdating || isOrderDisabled(index, delta)) {
      return;
    }
    const newQuestions = [...questions];
    newQuestions[index].order = index + delta;
    newQuestions[index + delta].order = index;
    setQuestions(newQuestions);
  };

  const uploadImage = (index) => {
    if (isUpdating) {
      return;
    }
    const fileUpload = document.querySelector(`#file_question_editor_${index}`);
    fileUpload.click();
  };

  const sortByOrder = (a, b) => a.order - b.order;

  const onChange = (value, index) => {
    const newQuestions = [...questions];
    newQuestions[index].body = value;
    setQuestions(newQuestions);
  };

  const getMissingId = (questions) => {
    let result = -1;
    questions.forEach((question, index) => {
      if (!question.id) {
        result = index;
      }
    });
    return result;
  };

  useEffect(() => {
    const index = getMissingId(questions);
    const newQuestions = [...questions];
    if (index !== -1 && newQuestions[index]) {
      newQuestions[index] = { ...data.QuizQuestions[index], order: index };
      setQuestions(newQuestions);
    }
  }, [JSON.stringify(data.QuizQuestions)]);

  const isDisabled = (id) => useAutoSave && !id;

  const mappedQuestions = questions.sort(sortByOrder).map((question, index) => (
    <QuestionWrapper key={`question_editor_${index}`}>
      <Question>
        <QuizNodeActivityEditor
          question={question}
          index={index}
          onChange={onChange}
          type={type}
          fileService={fileService}
          loading={!!loading}
          disabled={loading || isDisabled(question.id)}
        />
      </Question>
      <QuestionTools>
        <InsertImage
          src={UploadImageIcon}
          title={i18n.__('toolbar_button_tooltip_image')}
          onClick={() => uploadImage(index)}
          disabled={loading}
        />
        <DeleteQuestion
          src={RemoveIcon}
          title={i18n.__('app_delete')}
          onClick={() => removeQuestion(index)}
          disabled={loading}
        />
        <MoveUpQuestion
          src={MoveUpIcon}
          title={i18n.__('app_move_up')}
          onClick={() => orderQuestion(index, -1)}
          disabled={isOrderDisabled(index, -1)}
        />
        <MoveDownQuestion
          src={MoveUpIcon}
          title={i18n.__('app_move_down')}
          onClick={() => orderQuestion(index, 1)}
          disabled={isOrderDisabled(index, +1)}
        />
      </QuestionTools>
    </QuestionWrapper>
  ));

  return (
    <ContentSection>
      {children}
      <Questions>{mappedQuestions}</Questions>
    </ContentSection>
  );
};

export default WrittenQuizContentEditor;

WrittenQuizContentEditor.propTypes = {
  children: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  getUpdates: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  fileService: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  updating: PropTypes.bool,
};
